import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  NOTIFICATION_SHOW,
  SERVICE_FAILURE,
  ROLE_CREATE,
  ROLE_CREATE_COMPLETED,
  ROLE_CREATE_VIEW,
  ROLE_EDIT_MODE,
  ROLE_FIND,
  ROLE_FIND_ALL,
  ROLE_FIND_ALL_SUCCESS,
  ROLE_FIND_SUCCESS,
  ROLE_REMOVE,
  ROLE_REMOVE_COMPLETED,
  ROLE_RESET_ERROR,
  ROLE_UPDATE,
  ROLE_UPDATE_COMPLETED,
  ROLE_UPDATE_ID,
  ROLE_UPDATE_LOADING,
  ROLE_UPDATE_VIEW,
} from '@store/actions';
import { Rol, RolForm } from '@core/models';
import { AuthService, RoleService } from '@core/services';
import { selectRoleForm, selectRoleId } from '@store/selectors';
import { RolModuleEnum, RolModulePermissionEnum } from '@core/enums';

@Injectable()
export class RoleEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private roleService: RoleService,
    private authService: AuthService,
  ) {}

   createForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROLE_CREATE_VIEW),
      mergeMap(() => {
        return [
          ROLE_EDIT_MODE({ editMode: true, isEditing: false }),
        ];
      }),
    )
  );

  editForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROLE_UPDATE_VIEW),
      mergeMap(({ roleId }) => {
        return [
          ROLE_UPDATE_ID({ roleId }),
          ROLE_EDIT_MODE({ editMode: true, isEditing: true }),
        ];
      }),
    )
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROLE_FIND),
      filter((action) => action.id !== null),
      mergeMap(({ id }) => {
        this.store.dispatch(ROLE_UPDATE_LOADING({  loading: true }));

        return this.roleService.find(id).pipe(
          mergeMap(({ data }) => {
            const [role] = data as Rol[];

            return [
              ROLE_UPDATE_LOADING({ loading: false }),
              ROLE_FIND_SUCCESS({ role }),
            ];
          }),
          catchError((error) => 
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ROLE_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          )
        )
      }),
    )
  );

  findAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROLE_FIND_ALL),
      filter(() => this.authService.hasAnyRole([
        RolModuleEnum.Role, 
        RolModulePermissionEnum.CreateUser,
        RolModulePermissionEnum.EditUser,
      ])),  
      mergeMap(({ resellerId, search }) => {
        this.store.dispatch(ROLE_UPDATE_LOADING({  loading: true }));

        return this.roleService.findAll({ resellerId, search }).pipe(
          mergeMap(({ data }) => {
            const roleList = data as Rol[];

            return [
              ROLE_FIND_ALL_SUCCESS({ roleList }),
              ROLE_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError((error) => 
            of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ROLE_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          )
        )
      }),
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROLE_CREATE),
      withLatestFrom(
        this.store.select(selectRoleForm),
      ),
      filter(([_, roleForm ]) =>  !!roleForm),
      map(([_, roleForm]) => ({ roleForm: roleForm as RolForm })),
      mergeMap(({ roleForm }) => {
        this.store.dispatch(ROLE_UPDATE_LOADING({  loading: true }));

        return this.roleService.create(roleForm).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Role successfully created' } }),
              ROLE_UPDATE_LOADING({ loading: false }),
              ROLE_CREATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ROLE_RESET_ERROR(),
                  ROLE_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  creationComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROLE_CREATE_COMPLETED),
      tap(() => {
        this.roleService.creationCompleted();
      })
    ),
    { dispatch: false }
  )

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROLE_UPDATE),
      withLatestFrom(
        this.store.select(selectRoleId),
        this.store.select(selectRoleForm),
      ),
      filter(([_, roleId, roleForm ]) =>  !!roleId && !!roleForm),
      map(([_, roleId, roleForm]) => ({ id: roleId as string, roleForm: roleForm as RolForm })),
      mergeMap(({ id, roleForm }) => {
        this.store.dispatch(ROLE_UPDATE_LOADING({  loading: true }));

        return this.roleService.update(id, roleForm).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Role successfully updated' } }),
              ROLE_UPDATE_LOADING({ loading: false }),
              ROLE_UPDATE_COMPLETED(),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ROLE_RESET_ERROR(),
                  ROLE_UPDATE_LOADING({ loading: false }),
                ];
              })
            )
          })
        );
      }),
    )
  );

  updateComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROLE_UPDATE_COMPLETED),
      tap(() => {
        this.roleService.updateCompleted();
      })
    ),
    { dispatch: false }
  )

  remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROLE_REMOVE),
      filter(({ id }) => !!id),
      mergeMap(({ id }) => {

        return this.roleService.remove(id).pipe(
          mergeMap(() => {
            return [
              NOTIFICATION_SHOW({ notification: { type: 'success', title: 'Success', detail: 'Role removed successfully' } }),
              ROLE_REMOVE_COMPLETED(),
              ROLE_FIND_ALL({}),
            ];
          }),
          catchError((error) => {
            return of(error).pipe(
              mergeMap((error) => {
                return [
                  SERVICE_FAILURE({ error }),
                  ROLE_RESET_ERROR(),
                ];
              })
            )
          })
        );
      }),
    )
  );

  removeComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ROLE_REMOVE_COMPLETED),
      tap(() => {})
    ),
    { dispatch: false }
  )
}
